import { useState, type FormEvent, type ReactNode } from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import InputTextOnly from "../../controls/InputTextOnly";

export interface SetupRecordValues {
  [key: string]: string | boolean;
  status: boolean;
}

export interface SetupRecordField {
  name: string;
  label: ReactNode;
  placeholder?: string;
  required?: boolean;
}

interface SetupRecord {
  status?: boolean;
  [key: string]: unknown;
}

interface SaveResult {
  success: boolean;
  message?: string;
}

interface SetupRecordFormProps {
  title: ReactNode;
  description: ReactNode;
  record?: object | null;
  fields: readonly SetupRecordField[];
  onSave: (values: SetupRecordValues) => Promise<SaveResult>;
  onCancel: () => void;
}

export default function SetupRecordForm({
  title,
  description,
  record,
  fields,
  onSave,
  onCancel,
}: SetupRecordFormProps) {
  const typedRecord = record as SetupRecord | null | undefined;
  const [values, setValues] = useState<SetupRecordValues>(() => ({
    ...Object.fromEntries(fields.map((field) => [field.name, String(typedRecord?.[field.name] ?? "")])),
    status: typedRecord ? Boolean(typedRecord.status) : true,
  }));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSaving(true);
    setError("");
    const result = await onSave(values);
    setSaving(false);
    if (!result.success) setError(result.message ?? "Could not save this record.");
  };

  return (
    <form onSubmit={submit} className="flex h-full flex-col bg-white">
      <div className="flex items-start justify-between border-b border-gray-200 px-6 py-5">
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="mt-1 text-sm text-gray-500">{description}</p>
        </div>
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md p-1 text-gray-400 hover:bg-gray-100"
          aria-label="Close panel"
        >
          <XMarkIcon className="h-5 w-5" />
        </button>
      </div>
      <div className="flex-1 space-y-5 overflow-y-auto px-6 py-6">
        {error && (
          <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {error}
          </p>
        )}
        {fields.map((field) => (
          <InputTextOnly
            key={field.name}
            name={field.name}
            label={field.label}
            labelOn={true}
            required={field.required}
            value={String(values[field.name] ?? "")}
            onChange={(event) => setValues({ ...values, [field.name]: event.target.value })}
            placeholder={field.placeholder}
          />
        ))}
        <label className="flex items-start gap-3 rounded-md border border-gray-200 p-4">
          <input
            type="checkbox"
            checked={values.status}
            onChange={(event) => setValues({ ...values, status: event.target.checked })}
            className="mt-0.5 rounded border-gray-300 text-primary-600"
          />
          <span>
            <span className="block text-sm font-medium text-gray-900">Active</span>
            <span className="block text-xs text-gray-500">Available in receiving workflows.</span>
          </span>
        </label>
      </div>
      <div className="flex justify-end gap-3 border-t border-gray-200 px-6 py-4">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-gray-300 px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50"
        >
          Cancel
        </button>
        <button
          disabled={saving}
          className="rounded-md bg-main-purple px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-600 disabled:opacity-50"
        >
          {saving ? "Saving…" : "Save"}
        </button>
      </div>
    </form>
  );
}
